import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Phone, ChevronDown, ChevronRight, Clock,
  EyeOff, ShieldCheck, Heart, Award, Zap, Star, Users,
  ArrowUpRight, MessageSquare,
} from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/ui/motion";
import { SERVICE_CATEGORIES } from "@/data/services";
import { SERVICES_CONTENT } from "@/data/servicesContent";

const ease = [0.22, 1, 0.36, 1];

const ICON_MAP = { EyeOff, ShieldCheck, Clock, Heart, Award, Zap, Star, Users };

const FaqItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-slate-200 last:border-0">
    <button
      className="w-full flex items-center justify-between py-5 text-left gap-4"
      onClick={onToggle}
    >
      <span className="font-heading font-semibold text-slate-900">{q}</span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.25, ease }}
        className="shrink-0 text-slate-400"
      >
        <ChevronDown className="h-5 w-5" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease }}
          style={{ overflow: "hidden" }}
        >
          <p className="pb-5 text-sm text-slate-600 leading-relaxed">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ServiceDetailPageV2 = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  const cat = SERVICE_CATEGORIES.find((c) => c.slug === slug);
  const content = SERVICES_CONTENT[slug]?.[lang] || SERVICES_CONTENT[slug]?.fr;
  const [openFaq, setOpenFaq] = useState(null);

  if (!cat || !content) return <Navigate to="/services" replace />;

  const relatedCats = (content.relatedSlugs || [])
    .map((s) => SERVICE_CATEGORIES.find((c) => c.slug === s))
    .filter(Boolean);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: lang === "fr" ? "Accueil" : "Home",
        item: "https://bionet-solutions.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://bionet-solutions.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: t(`categories.${slug}.name`),
        item: `https://bionet-solutions.com/services/${slug}`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={content.seo.title}
        description={content.seo.description}
        path={`/services/${slug}`}
        schema={breadcrumbSchema}
      />

      {/* ── 1. HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50/40">
        <div className="absolute -top-32 -right-32 h-[30rem] w-[30rem] rounded-full bg-emerald-200/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 h-[20rem] w-[20rem] rounded-full bg-sky-200/20 blur-3xl pointer-events-none" />

        <div className="container-x pt-8 pb-16 lg:pt-14 lg:pb-24">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8" aria-label="breadcrumb">
            <Link to="/" className="hover:text-brand-green transition-colors whitespace-nowrap">
              {lang === "fr" ? "Accueil" : "Home"}
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            <Link to="/services" className="hover:text-brand-green transition-colors whitespace-nowrap">
              Services
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            <span className="text-slate-900 font-medium truncate">
              {t(`categories.${slug}.name`)}
            </span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left -text */}
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease }}
            >
              <div className="pill mb-5 w-fit">
                <span>{content.hero.badge}</span>
              </div>
              <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-[2.65rem] xl:text-5xl text-slate-900 leading-[1.05]">
                {content.hero.h1}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                {content.hero.subtitle}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+33649502801"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green text-white px-6 py-3.5 text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  data-testid="v2-hero-phone"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  06 49 50 28 01
                </a>
                <Link
                  to="/devis"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-slate-700 px-6 py-3.5 text-sm font-semibold hover:border-brand-green hover:text-brand-green transition-colors group whitespace-nowrap"
                  data-testid="v2-hero-quote"
                >
                  {lang === "fr" ? "Devis gratuit" : "Free quote"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* Right -image */}
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, scale: 0.94, x: 24 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease }}
            >
              <div className="relative">
                <div className="absolute -inset-3 rounded-[32px] bg-gradient-to-br from-emerald-200/50 to-sky-200/40 blur-2xl opacity-60" />
                <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
                  <img
                    src={content.hero.image || "https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=900&fit=crop"}
                    alt={t(`categories.${slug}.name`)}
                    className="w-full h-[380px] object-cover"
                    loading="eager"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=900&fit=crop";
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. INTRODUCTION ─────────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x max-w-4xl">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-8">
              {content.intro.title}
            </h2>
          </FadeUp>
          <div className="space-y-5">
            {content.intro.body.map((para, i) => (
              <FadeUp key={i} delay={i * 0.07}>
                <p className={i === 0 ? "text-lg text-slate-700 leading-relaxed" : "text-base text-slate-600 leading-relaxed"}>
                  {para}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. POURQUOI NOUS ────────────────────────────────────────── */}
      <section className="section bg-slate-50">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-12">
              {lang === "fr" ? "Pourquoi nous choisir ?" : "Why choose us?"}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {content.whyUs.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || ShieldCheck;
              return (
                <StaggerItem key={i}>
                  <motion.div
                    className="rounded-3xl bg-white border border-slate-200 p-7 ring-soft h-full"
                    whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                    data-testid={`why-us-${i}`}
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
                      <Icon className="h-5 w-5 text-brand-green" />
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-slate-900 leading-snug">{item.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </motion.div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 4. PROCESSUS ────────────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-14">
              {lang === "fr" ? "Notre processus d'intervention" : "Our intervention process"}
            </h2>
          </FadeUp>
          <div className="relative">
            <div className="hidden xl:block absolute top-5 left-[6%] right-[6%] h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {content.process.map((step, i) => (
                <FadeUp key={i} delay={i * 0.1}>
                  <div>
                    <motion.div
                      className="h-10 w-10 rounded-full bg-brand-green text-white flex items-center justify-center font-heading font-bold text-sm mb-3 relative z-10"
                      initial={{ scale: 0.6, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, ease, delay: i * 0.1 }}
                    >
                      {step.step}
                    </motion.div>
                    <div className="mb-3 inline-block px-2.5 py-1 rounded-full bg-emerald-50 text-[11px] font-semibold text-brand-green tracking-wide">
                      {step.tag}
                    </div>
                    <h4 className="font-heading font-bold text-slate-900 mb-2 leading-snug">{step.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. EXEMPLES CLIENTS ─────────────────────────────────────── */}
      <section className="section bg-slate-50">
        <div className="container-x">
          <FadeUp>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-4 w-4 text-brand-green" />
              <span className="text-xs font-bold text-brand-green uppercase tracking-widest">
                {lang === "fr" ? "Exemples de demandes" : "Request examples"}
              </span>
            </div>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-12">
              {lang === "fr" ? "Ils nous ont fait confiance" : "They trusted us"}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {content.clientExamples.map((ex, i) => (
              <StaggerItem key={i}>
                <motion.div
                  className="rounded-3xl bg-white border border-slate-200 p-7 ring-soft h-full flex flex-col"
                  whileHover={{ y: -3, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                  data-testid={`client-example-${i}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
                      {ex.avatar}
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-slate-900 text-sm">{ex.from}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{ex.date}</div>
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 pb-3 border-b border-slate-100">
                    {lang === "fr" ? "Objet" : "Subject"} : {ex.subject}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-5 flex-1">{ex.message}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 6. FAQ ──────────────────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x max-w-3xl">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-8">
              {lang === "fr" ? "Questions fréquentes" : "Frequently asked questions"}
            </h2>
          </FadeUp>
          <div className="rounded-3xl bg-white border border-slate-200 ring-soft px-6 sm:px-8">
            {content.faq.map((item, i) => (
              <FaqItem
                key={i}
                q={item.q}
                a={item.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. SERVICES LIÉS ────────────────────────────────────────── */}
      {relatedCats.length > 0 && (
        <section className="section bg-slate-50">
          <div className="container-x">
            <FadeUp>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-8">
                {lang === "fr" ? "Services associés" : "Related services"}
              </h3>
            </FadeUp>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedCats.map((o) => {
                const I = o.icon;
                return (
                  <motion.div
                    key={o.slug}
                    whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                  >
                    <Link
                      to={`/services/${o.slug}`}
                      className="group block h-full rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-blue/40 hover:shadow-md transition-all ring-soft"
                      data-testid={`related-v2-${o.slug}`}
                    >
                      <I className="h-5 w-5 text-brand-green" />
                      <div className="mt-3 font-heading font-semibold text-slate-900">
                        {t(`categories.${o.slug}.name`)}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                        {t(`categories.${o.slug}.tagline`)}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-green">
                        {lang === "fr" ? "Découvrir" : "Discover"}
                        <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── 8. CTA FINAL ────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-[#166534] via-emerald-700 to-sky-800">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-sky-300/20 blur-3xl" />
        </div>
        <div className="container-x relative text-center">
          <FadeUp>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-sm text-emerald-100 mb-6">
              <Clock className="h-3.5 w-3.5" />
              {lang === "fr" ? "Devis gratuit sous 2h" : "Free quote within 2h"}
            </div>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-white">
              {lang === "fr"
                ? "Prêt à nous confier votre intervention ?"
                : "Ready to entrust your project to our experts?"}
            </h2>
            <p className="mt-4 text-emerald-100 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              {lang === "fr"
                ? "Devis gratuit, réponse en moins de 2h. Disponibles 24h/24, 7j/7 à Lyon et en Rhône-Alpes."
                : "Free quote, response in under 2h. Available 24/7 in Lyon and the Rhône-Alpes region."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#166534] font-semibold px-7 py-3.5 text-sm hover:bg-slate-50 transition-colors group"
                data-testid="v2-final-cta-quote"
              >
                {lang === "fr" ? "Demander un devis gratuit" : "Get a free quote"}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+33649502801"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-semibold px-7 py-3.5 text-sm hover:bg-white/10 transition-colors"
                data-testid="v2-final-cta-phone"
              >
                <Phone className="h-4 w-4" />
                06 49 50 28 01
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default ServiceDetailPageV2;
